// Core
import React from 'react';

// Libraries
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Types
import { Theme } from '../../../../../../../../types/theme';

// Hooks
import { useBreakpoint } from '../../../../../../../../hooks/useBreakpoint';

// Plugins
import {
  booleanToEnabled,
  booleanToEnableStatus,
} from '../../../../../../../../plugins/crusher';

// Components
import { EnableItem } from '../../../../../../../../components/EnableItem';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../../../layout/elements/Paragraph';

// Component
import { ControlModeProps } from './ControlMode.types';
import {
  Container,
  EnableItemsContainer,
  LabelsContainer,
  Separator,
} from './ControlMode.style';

function ControlMode({
  className,
  unitData,
  edgeStatus,
  testId,
}: ControlModeProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  return (
    <Container className={className} data-testid={testId}>
      <Paragraph
        size={
          isXs
            ? ParagraphSize.sm
            : ParagraphSize.lg
        }
        weight={ParagraphWeight.bold}
      >
        {t('view.crusher.maintenance.setup.controlMode.title')}
      </Paragraph>
      <LabelsContainer>
        <Paragraph
          size={
            isXs
              ? ParagraphSize.xs
              : ParagraphSize.sm
          }
          color={
            theme.color.crusherDashboard.maintenance.setup.controlMode.descriptionTextColor
          }
        >
          {t('common.status')}
        </Paragraph>
        <Paragraph
          size={
            isXs
              ? ParagraphSize.xs
              : ParagraphSize.sm
          }
          color={
            theme.color.crusherDashboard.maintenance.setup.controlMode.descriptionTextColor
          }
        >
          {t('common.sensor')}
        </Paragraph>
      </LabelsContainer>
      <Separator />
      <EnableItemsContainer>
        <EnableItem
          description={t('view.crusher.maintenance.setup.controlMode.linerWearDescription')}
          enabled={booleanToEnabled(unitData?.linerWearEnable)}
          status={booleanToEnableStatus(
            unitData?.linerWearEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp ? unitData?.timestamp : null,
          )}
        />
        <EnableItem
          description={t(
            'view.crusher.maintenance.setup.controlMode.counterShaftDescription',
          )}
          enabled={booleanToEnabled(unitData?.countershaftBlowerRunningStatus)}
          status={booleanToEnableStatus(
            unitData?.countershaftBlowerRunningStatus,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp ? unitData?.timestamp : null,
          )}
        />
        <EnableItem
          description={t(
            'view.crusher.maintenance.setup.controlMode.feedControlDescription',
          )}
          enabled={booleanToEnabled(unitData?.feedControlVfdEnable)}
          status={booleanToEnableStatus(
            unitData?.feedControlVfdEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp ? unitData?.timestamp : null,
          )}
        />
        <EnableItem
          description={t(
            'view.crusher.maintenance.setup.controlMode.dischargeEnableDescription',
          )}
          enabled={booleanToEnabled(unitData?.dischargeSuperiorEnable)}
          status={booleanToEnableStatus(
            unitData?.dischargeSuperiorEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp ? unitData?.timestamp : null,
          )}
        />
        <EnableItem
          description={t('view.crusher.maintenance.setup.controlMode.feedEnableDescription')}
          enabled={booleanToEnabled(unitData?.feedSuperiorEnable)}
          status={booleanToEnableStatus(
            unitData?.feedSuperiorEnable,
            edgeStatus?.edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp ? unitData?.timestamp : null,
          )}
        />
      </EnableItemsContainer>
    </Container>
  );
}

export { ControlMode };
